import React from "react";

import { useDropdownMenuHandlers } from "../../helpers/dropdownMenuHandlers";
import { useOutsideClick } from "../../helpers/useOutsideClick";
import { useUIDSeed } from "../../helpers/useUIDSeed";
import { Button } from "../Button";
import { Card } from "../Card";
import { Icon, IconDictionary, SIZES } from "../Icon";

import styles from "./InfoModal.scss";

const iconColor = styles.systemDefault;

interface ModalButtonProps {
  id: string;
  triggerClickHandler: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  dropdownMenuId: string;
  isOpen: boolean;
  ariaLabelIds: string;
  triggerKeyDownHandler: (event: React.KeyboardEvent) => void;
  icon: IconDictionary;
  triggerLabelId: string;
  label: React.ReactNode;
}

function ModalButton({
  id,
  triggerClickHandler,
  triggerRef,
  dropdownMenuId,
  isOpen,
  ariaLabelIds,
  triggerKeyDownHandler,
  icon,
  triggerLabelId,
  label,
}: ModalButtonProps) {
  return (
    <Button
      id={id}
      className={
        isOpen && icon === "caret_dn"
          ? styles["info-modal__hidden"]
          : styles["info-modal__trigger"]
      }
      size="default"
      variant="transparentDark"
      onClick={triggerClickHandler}
      innerRef={triggerRef}
      aria-haspopup="listbox"
      aria-controls={dropdownMenuId}
      aria-expanded={isOpen}
      aria-labelledby={ariaLabelIds}
      onKeyDown={triggerKeyDownHandler}
      data-testid="info-modal__button"
    >
      <span id={triggerLabelId} data-testid="info-modal__label">
        {label}
      </span>
      <Icon
        icon={icon}
        size={SIZES.Small}
        color={iconColor}
        className={styles["trigger-button-icon"]}
      />
    </Button>
  );
}

/**
 * InfoModal Component
 *
 * This component displays an info modal with a button trigger and a card-based popup.
 *
 * @param props - The component props
 * @param props.id - Optional ID for the modal
 * @param props.ariaLabelledby - Optional ARIA labelledby attribute
 * @param props.label - The label for the button that opens/closes the modal
 * @param props.text - The text that appears in the body of the modal once it's open
 * @param props.closeOnClickAway - Whether to close the modal when clicking outside
 */
export interface InfoModalProps {
  id?: string;
  ariaLabelledby?: string;
  label: React.ReactNode;
  text: React.ReactNode;
  closeOnClickAway?: boolean;
}

export function InfoModal({
  id,
  ariaLabelledby,
  label,
  text,
  closeOnClickAway,
}: InfoModalProps) {
  const generateUID = useUIDSeed(id, ariaLabelledby, label, text);
  const infoModalTriggerId = id || generateUID("info-modal-trigger");
  const infoModalTriggerLabelId = `inner-${infoModalTriggerId}-label`;
  const infoModalMenuId = id
    ? `info-modal-menu-${id}`
    : generateUID("info-modal-menu");

  const ariaLabelIds = [ariaLabelledby, infoModalTriggerLabelId]
    .filter((id) => id)
    .join(" ");

  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const [isOpen, setOpen] = React.useState(false);

  const focusOnTrigger = () => {
    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  useOutsideClick({
    elementRefs: [triggerRef, containerRef],
    handler: () => {
      if (closeOnClickAway && isOpen) {
        setOpen(false);
      }
    },
    dependencies: [isOpen],
    ignoreEffect: !isOpen,
  });

  const infoModalMenuHandlers = useDropdownMenuHandlers({
    closeMenu: () => setOpen(false),
    focusOnTrigger,
    openMenu: () => setOpen(true),
    toggleMenu: () => setOpen(!isOpen),
    options: [], // Add empty options array to satisfy the type
  });

  const { triggerClickHandler, triggerKeyDownHandler } = infoModalMenuHandlers;

  return (
    <div
      role="presentation"
      className={styles["info-modal"]}
      data-testid="info-modal"
      ref={containerRef}
    >
      <ModalButton
        id={infoModalTriggerId}
        triggerClickHandler={triggerClickHandler}
        triggerRef={triggerRef}
        dropdownMenuId={infoModalMenuId}
        isOpen={isOpen}
        ariaLabelIds={ariaLabelIds}
        triggerKeyDownHandler={triggerKeyDownHandler}
        icon="caret_dn"
        triggerLabelId={infoModalTriggerLabelId}
        label={label}
      />
      {isOpen && (
        <Card className={styles["info-modal__open"]}>
          <ModalButton
            id={infoModalTriggerId}
            triggerClickHandler={triggerClickHandler}
            triggerRef={triggerRef}
            dropdownMenuId={infoModalMenuId}
            isOpen={isOpen}
            ariaLabelIds={ariaLabelIds}
            triggerKeyDownHandler={triggerKeyDownHandler}
            icon="caret_up"
            triggerLabelId={infoModalTriggerLabelId}
            label={label}
          />
          <div
            className={styles["info-modal__body"]}
            data-testid="info-modal__body"
          >
            {text}
          </div>
        </Card>
      )}
    </div>
  );
}
